import { useEffect, useMemo, useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { ScreeningCostEstimates } from "@/services/api/guardiansv2/types";
import {
  AVAILABLE_MODELS,
  getProviderForModel,
  MODEL_OPTIONS_BY_PROVIDER,
} from "@/constants/llmModels";
import { Button } from "@/components/ui/button/Button";

const DEFAULT_SCREENING_MODEL = "claude-sonnet-4-6";

const CATALOG_MODEL_IDS: Set<string> = new Set(AVAILABLE_MODELS.map((m) => m.value));

function modelLabel(modelId: string): string {
  return AVAILABLE_MODELS.find((m) => m.value === modelId)?.label ?? modelId;
}

function formatUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "$0.00";
  if (value >= 0.1) return `$${value.toFixed(2)}`;
  if (value >= 0.01) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(4)}`;
}

function costRangeLine(
  modelId: string,
  estimates: ScreeningCostEstimates | undefined
): string | null {
  const row = estimates?.models.find((m) => m.model === modelId);
  if (!row) return null;
  return `Estimated cost per screening: ~${formatUsd(row.typical_usd)} (typical workload) to ~${formatUsd(row.heavy_usd)} (heavy workload). Approximate; standard API rates.`;
}

/** Alert screening settings for completion triage alerts. */
export function ScreeningPanel() {
  const { showSuccess, showError } = useNotification();
  const [enabled, setEnabled] = useState(false);
  const [model, setModel] = useState(DEFAULT_SCREENING_MODEL);
  const [costEstimates, setCostEstimates] = useState<ScreeningCostEstimates | undefined>();
  const [saving, setSaving] = useState(false);

  const isOffCatalog = !CATALOG_MODEL_IDS.has(model);

  useEffect(() => {
    GuardiansV2API.getSettings()
      .then((s) => {
        const ps = s.screening;
        const legacyDisabled = (ps?.mode as string | undefined) === "disabled";
        setEnabled(legacyDisabled ? false : (ps?.enabled ?? false));
        setModel(s.screening_model?.model ?? DEFAULT_SCREENING_MODEL);
        setCostEstimates(s.screening_cost_estimates);
      })
      .catch(() => {});
  }, []);

  const selectedCostLine = useMemo(() => costRangeLine(model, costEstimates), [model, costEstimates]);

  const allModelsCostSummary = useMemo(() => {
    if (!costEstimates?.models.length) return null;
    return costEstimates.models
      .map((row) => `${modelLabel(row.model)}: ${formatUsd(row.typical_usd)}–${formatUsd(row.heavy_usd)}`)
      .join(" · ");
  }, [costEstimates]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await GuardiansV2API.patchSettings({
        screening: {
          enabled,
          mode: "all",
        },
        screening_model: {
          provider: getProviderForModel(model),
          model,
        },
      });
      setCostEstimates(updated.screening_cost_estimates);
      showSuccess("Alert screening settings saved");
    } catch {
      showError("Failed to save alert screening settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="screening-panel">
      <div>
        <h3 className="text-sm font-medium text-slate-200">Alert screening</h3>
        <p className="mt-1 text-sm text-slate-400">
          After each terminal task and at mission completion, Guardians evaluates LLM alert rules
          and programmatic failure signals (tool errors, blocked tasks, and similar).
        </p>
      </div>
      <label className="flex cursor-pointer items-start justify-between gap-3 rounded-lg border border-white/5 bg-slate-800/40 px-3 py-2">
        <span>
          <span className="block text-sm text-slate-200">Alert screening enabled</span>
          <span className="block text-xs text-slate-500">
            Raise alerts when checks fail or unmatched programmatic issues are detected
          </span>
        </span>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-slate-500"
        />
      </label>
      <div className="rounded-lg bg-slate-800/50 p-3">
        <label className="block text-sm font-medium text-slate-200" htmlFor="screening-model">
          Screening model
        </label>
        <p className="mt-1 text-xs text-slate-500">
          LLM for batched alert-rule evaluation. Defaults to {modelLabel(DEFAULT_SCREENING_MODEL)} when unset.
        </p>
        <select
          id="screening-model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="mt-2 w-full rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-slate-100"
          data-testid="screening-model-select"
        >
          {isOffCatalog ? <option value={model}>{model} (current)</option> : null}
          {MODEL_OPTIONS_BY_PROVIDER.map((group) => (
            <optgroup key={group.provider} label={group.provider}>
              {group.models.map((modelId) => (
                <option key={modelId} value={modelId}>
                  {modelLabel(modelId)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {selectedCostLine ? (
          <p className="mt-2 text-xs text-slate-400" data-testid="screening-model-cost-line">
            {selectedCostLine}
          </p>
        ) : null}
        {allModelsCostSummary ? (
          <p className="mt-2 text-[11px] leading-relaxed text-slate-600">{allModelsCostSummary}</p>
        ) : null}
      </div>
      <Button onClick={() => void save()} disabled={saving} className="w-full">
        {saving ? "Saving…" : "Save alert screening"}
      </Button>
    </div>
  );
}
